import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { Press } from '@dorixona/ui'
import { GapRow } from '../../ui/Gaps'
import { formatAmount, formatDayTime, formatMoney, formatPhone } from '@dorixona/utils'
import { AutoTextarea } from '../../ui/AutoTextarea'
import { Icon } from '../../ui/Icon'
import { DetailHead } from '../../ui/DetailHead'
import { Modal } from '../../ui/Modal'
import { Loading, failureText } from '../../ui/Load'
import { Select } from '../../ui/Select'
import { useSession } from '../auth/session'
import { useStaff } from '../admins/api'
import { useCustomer } from '../customers/api'
import { ledgerText } from '../customers/model'
import { useStores } from '../stores/api'
import { hoursText, storeTitle } from '../stores/model'
import { OrderNow } from './OrderNow'
import type { NextStep } from './stage'
import { CustomerPanel } from './CustomerPanel'
import {
  useAssemble,
  useCallCourier,
  useChangeStatus,
  useDeclineOrder,
  useIssueOrder,
  useOrderTransactions,
  useOrder,
  useBoard,
  useOrders,
  usePickLine,
  usePrintSheet,
  useReleaseHold,
  useRouteOrder,
  useTakeOrder,
  type DeclineReason,
  type Order,
  type PrintSheet,
} from './api'
import {
  PAY_AT_PHARMACY,
  STATUS_TONE,
  actorText,
  canRelease,
  isDone,
  slaView,
  payStatusText,
  payText,
  pointsPayText,
  providerText,
  reservationText,
  shipText,
  statusText,
} from './model'
import { listAddress } from '../../app/listMemory'

export interface OrderScreenProps {
  onToast: (message: string) => void
}

/** Причины отказа аптеки — те, что принимает `POST /admin/orders/{id}/decline`. */
const DECLINE_REASONS: { value: DeclineReason; label: string }[] = [
  { value: 'out_of_stock', label: 'Нет в наличии' },
  { value: 'damaged', label: 'Товар повреждён' },
  { value: 'other', label: 'Другое' },
]

/**
 * Карточка заказа: состав, сборка, доставка, история и всё, что о клиенте и
 * оплате нужно знать, не уходя с экрана.
 *
 * Слева — то, с чем работает аптека: состав, чек-лист сборки, история. Справа
 * — справка: клиент, получение, оплата и баллы. Действия по заказу — переходы
 * статуса, поэтому общей кнопки «Сохранить» здесь нет: каждое уходит сразу.
 */
export function OrderScreen({ onToast }: OrderScreenProps) {
  const { can, storeId: myStore } = useSession()
  const navigate = useNavigate()
  const params = useParams()
  const id = Number(params.id)
  const [issuing, setIssuing] = useState(false)
  const [declining, setDeclining] = useState(false)
  const [releasing, setReleasing] = useState(false)
  const [reason, setReason] = useState<DeclineReason>('out_of_stock')
  const [comment, setComment] = useState('')
  const [sheet, setSheet] = useState<PrintSheet | null>(null)

  const order = useOrder(id)
  const staff = useStaff()
  const stores = useStores()
  const customerId = order.data?.customerId ?? 0
  const customer = useCustomer(can('customers') ? customerId : 0)
  // Нужно только число: сервер отдаёт `total`, строк хватит одной.
  const history = useOrders({ customerId, pageSize: 1 }, customerId > 0 && can('orders'))
  const ledger = useOrderTransactions(id, id > 0)
  // Таймер этапа (BR-20) сервер считает для доски, а не для карточки: берём
  // его оттуда. Завершённого заказа на доске нет — и запрос не нужен.
  const active = order.data !== undefined && !isDone(order.data.status)
  const board = useBoard(order.data?.storeId ?? 0, '', active && can('orders'))

  const take = useTakeOrder()
  const pick = usePickLine()
  const assemble = useAssemble()
  const change = useChangeStatus()
  const issue = useIssueOrder()
  const decline = useDeclineOrder()
  const route = useRouteOrder()
  const call = useCallCourier()
  const print = usePrintSheet()
  const release = useReleaseHold()

  // Лист уже в разметке (он виден только на бумаге) — теперь можно печатать.
  // После печати он не нужен: следующая печать возьмёт свежий заказ с сервера.
  useEffect(() => {
    if (!sheet) return
    const printed = () => {
      setSheet(null)
    }
    window.addEventListener('afterprint', printed)
    window.print()
    return () => {
      window.removeEventListener('afterprint', printed)
    }
  }, [sheet])

  function done(next: Order) {
    onToast(`№ ${next.number}: ${statusText(next.status).toLowerCase()}`)
  }
  function failed(error: unknown) {
    onToast(failureText(error))
  }
  const handlers = { onSuccess: done, onError: failed }

  if (order.isPending) return <Loading what="заказ" />
  if (order.isError) {
    return (
      <div className="ui-card">
        <div className="ui-empty">
          <Icon name="i-box" />
          <b>Заказ не найден</b>
          <p>{failureText(order.error)}</p>
          <button
            type="button"
            className="ui-btn"
            data-variant="secondary"
            onClick={() => {
              void navigate(listAddress('/orders'))
            }}
          >
            К списку заказов
          </button>
        </div>
      </div>
    )
  }

  const item = order.data
  const canUpdate = can('orders', 'u')
  const closed = isDone(item.status)
  const card = board.data?.flatMap((column) => column.orders).find((o) => o.id === item.id)
  const sla = slaView(card?.sla ?? null)
  const allStores = stores.data ?? []
  const store = allStores.find((s) => s.id === item.storeId)
  const storeName = item.storeName || 'не назначена'
  const ledgerEntries = ledger.data ?? []
  const assignee = staff.data?.find((a) => a.id === item.assigneeId)?.name ?? '—'
  const delivery = item.ship === 'delivery'
  // Отказаться аптека может, пока заказ не собран (R-37).
  const canDecline = canUpdate && (item.status === 'new' || item.status === 'assembling')
  // Вручную назначает аптеку только сотрудник всей сети — зависшему заказу (R-38).
  const canRoute = canUpdate && myStore === 0 && item.status === 'needs_attention'
  const cashAtPharmacy = item.payMethod === PAY_AT_PHARMACY
  const releasable = canRelease(item, myStore, canUpdate)

  function runStep(action: NextStep['action']) {
    if (action === 'take') return take.mutate(id, handlers)
    if (action === 'assemble') return assemble.mutate(id, handlers)
    if (action === 'call') return call.mutate(id, handlers)
    if (action === 'courier') return change.mutate({ id, to: 'in_delivery' }, handlers)
    if (action === 'delivered') return change.mutate({ id, to: 'delivered' }, handlers)
    setIssuing(true)
  }

  return (
    <>
      <DetailHead
        backLabel="Заказы"
        onBack={() => {
          void navigate(listAddress('/orders'))
        }}
        title={`№ ${item.number}`}
        sub={
          <>
            <span className="ui-status" data-tone={STATUS_TONE[item.status]}>
              {statusText(item.status)}
            </span>
            <span className="ui-method" data-method={item.ship}>
              <Icon name={delivery ? 'i-truck' : 'i-pin'} size={14} />
              {shipText(item.ship)}
            </span>
            {item.hasRx ? (
              <span className="ui-tag" data-tone="rx">
                ℞ в заказе
              </span>
            ) : null}
            <span className="dot" aria-hidden="true" />
            <span className="od-strong">{formatMoney(item.total)}</span>
            <span className="dot" aria-hidden="true" />
            <span className="od-num">{formatDayTime(item.createdAt)}</span>
          </>
        }
        actions={
          <>
            <Press
              as="button"
              type="button"
              className="ui-btn"
              data-variant="secondary"
              squish="card"
              disabled={print.isPending}
              onPress={() => {
                print.mutate(id, { onSuccess: setSheet, onError: failed })
              }}
            >
              <Icon name="i-note" />
              Печать
            </Press>
          </>
        }
      />

      <OrderNow
        order={item}
        canUpdate={canUpdate && !closed}
        canDecline={canDecline}
        routeOptions={
          canRoute
            ? [
                { value: item.storeId, label: storeName },
                ...allStores
                  .filter((s) => s.id !== item.storeId)
                  .map((s) => ({ value: s.id, label: storeTitle(s) })),
              ]
            : null
        }
        sla={sla}
        assignee={assignee}
        busy={[take, pick, assemble, change, call].some((m) => m.isPending)}
        onStep={runStep}
        onPick={(lineId) => {
          pick.mutate({ id, lineId }, { onError: failed })
        }}
        onDecline={() => {
          setDeclining(true)
        }}
        onRoute={(storeId) => {
          route.mutate({ id, storeId }, handlers)
        }}
      />

      <div className="ui-detail-grid">
        <div className="ui-detail-col">
          <section className="ui-table-card od-lines" aria-labelledby="od-lines">
            <div className="ui-card-head">
              <h3 id="od-lines">
                Состав заказа <span className="ui-count">{item.lines.length}</span>
              </h3>
            </div>
            <div className="ui-table-wrap">
              <table className="ui-table">
                <thead>
                  <tr>
                    <th scope="col">Товар</th>
                    <th scope="col" data-align="end">
                      Цена
                    </th>
                    <th scope="col" data-align="end">
                      Кол-во
                    </th>
                    <th scope="col" data-align="end">
                      Сумма
                    </th>
                    <th scope="col" data-align="end">
                      Кэшбэк
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {item.lines.map((line) => {
                    const href = `/products/${line.productId}`
                    return (
                      <tr
                        key={line.id}
                        data-href={href}
                        onClick={() => {
                          void navigate(href)
                        }}
                      >
                        <td>
                          <Link className="primary" to={href}>
                            {line.name}
                          </Link>
                          {line.picked ? <span className="secondary">собрано</span> : null}
                        </td>
                        <td className="num" data-align="end">
                          {formatAmount(line.price)}
                        </td>
                        <td className="num" data-align="end">
                          {line.qty}
                        </td>
                        <td className="num od-strong" data-align="end">
                          {formatAmount(line.total)}
                        </td>
                        <td className="num od-cb" data-align="end">
                          {line.cashback ? `+${formatAmount(line.cashback)}` : '—'}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            <div className="ui-card-body od-totals">
              <div className="ui-facts">
                <div>
                  <span className="k">Товары</span>
                  <span className="v od-num">{formatMoney(item.goods)}</span>
                </div>
                <div>
                  <span className="k">{shipText(item.ship)}</span>
                  <span className="v od-num">
                    {item.deliveryFee ? formatMoney(item.deliveryFee) : 'Бесплатно'}
                  </span>
                </div>
                {item.spend ? (
                  <div>
                    <span className="k">Оплачено баллами</span>
                    <span className="v od-num">−{formatMoney(item.spend)}</span>
                  </div>
                ) : null}
                <div className="od-total">
                  <span className="k">Итого к оплате</span>
                  <span className="v od-num">{formatMoney(item.total)}</span>
                </div>
              </div>
            </div>
          </section>

          <section className="ui-card" aria-labelledby="od-history">
            <div className="ui-card-head">
              <h3 id="od-history">История заказа</h3>
            </div>
            <div className="ui-card-body">
              {item.events.length === 0 ? (
                <p className="ui-muted-line">Событий пока нет</p>
              ) : (
                <ol className="od-history">
                  {item.events.map((event) => (
                    <li key={event.id}>
                      <b>{event.message}</b>
                      <span className="od-when">
                        {formatDayTime(event.at)} · {actorText(event.actor)}
                      </span>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </section>
        </div>

        <div className="ui-detail-col">
          <CustomerPanel
            customer={customer.data?.customer}
            name={item.customerName}
            phone={item.customerPhone}
            balance={customer.data?.balance ?? 0}
            ordersCount={history.data?.total ?? null}
            onOpen={() => {
              void navigate(`/customers/${item.customerId}`)
            }}
          />

          <section className="ui-card" aria-labelledby="od-ship">
            <div className="ui-card-head">
              <h3 id="od-ship">{delivery ? 'Доставка' : 'Выдача в аптеке'}</h3>
            </div>
            <div className="ui-card-body od-facts-body">
              <div className="ui-facts">
                {delivery ? (
                  <>
                    <div>
                      <span className="k">Адрес</span>
                      <span className="v">{item.address || 'адрес не указан'}</span>
                    </div>
                    <div>
                      <span className="k">Аптека-отправитель</span>
                      <span className="v">{storeName}</span>
                    </div>
                    <div>
                      <span className="k">Трек-номер</span>
                      <span className="v">
                        {item.delivery?.tracking ? (
                          <>
                            <span className="ui-code">{item.delivery.tracking}</span>{' '}
                            {providerText(item.delivery.provider)}
                          </>
                        ) : (
                          'появится, когда вызовут курьера'
                        )}
                      </span>
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <span className="k">Аптека</span>
                      <span className="v">
                        <Link className="od-link" to={`/stores/${item.storeId}`}>
                          {storeName}
                        </Link>
                      </span>
                    </div>
                    <div>
                      <span className="k">Адрес</span>
                      <span className="v">{store?.address ?? '—'}</span>
                    </div>
                    <div>
                      <span className="k">Режим работы</span>
                      <span className="v od-num">{store ? hoursText(store) : '—'}</span>
                    </div>
                    <div>
                      <span className="k">Телефон аптеки</span>
                      <span className="v od-num">{formatPhone(store?.phone ?? '') || '—'}</span>
                    </div>
                    <div>
                      <span className="k">Бронь до</span>
                      <span className="v od-num">{reservationText(item)}</span>
                    </div>
                    <GapRow what="Код выдачи" where="Order в GET /admin/orders/{id}" />
                  </>
                )}
                <div>
                  <span className="k">Сборщик</span>
                  <span className="v">{assignee}</span>
                </div>
                <GapRow
                  what="Комментарий клиента и оценка заказа"
                  where="Order в GET /admin/orders/{id}"
                />
              </div>
            </div>
          </section>

          <section className="ui-card" aria-labelledby="od-pay">
            <div className="ui-card-head">
              <h3 id="od-pay">Оплата</h3>
              {releasable ? (
                <button
                  type="button"
                  className="ui-btn"
                  data-variant="secondary"
                  data-size="sm"
                  disabled={release.isPending}
                  aria-busy={release.isPending}
                  onClick={() => {
                    setReleasing(true)
                  }}
                >
                  Вернуть деньги
                </button>
              ) : null}
            </div>
            <div className="ui-card-body od-facts-body">
              <div className="ui-facts">
                <div>
                  <span className="k">Способ</span>
                  <span className="v">{payText(item.payMethod) || '—'}</span>
                </div>
                {item.spend ? (
                  <div>
                    <span className="k">Баллы</span>
                    <span className="v">{pointsPayText(item)}</span>
                  </div>
                ) : null}
                <div>
                  <span className="k">Состояние</span>
                  <span className="v">{payStatusText(item.payStatus)}</span>
                </div>
                {item.paidAt ? (
                  <div>
                    <span className="k">Оплачен</span>
                    <span className="v od-num">{formatDayTime(item.paidAt)}</span>
                  </div>
                ) : null}
                <div>
                  <span className="k">Сумма списания</span>
                  <span className="v od-num">{formatMoney(item.total)}</span>
                </div>
                <GapRow
                  what="Платёжный шлюз и ID транзакции"
                  where="Order в GET /admin/orders/{id}"
                />
              </div>
            </div>
          </section>

          <section className="ui-card" aria-labelledby="od-points">
            <div className="ui-card-head">
              <h3 id="od-points">Баллы</h3>
              {/* Когда кэшбэк дойдёт до клиента (R-52) — коротко, в шапке. */}
              <span className="aside">
                <Icon name={closed ? 'i-check' : 'i-clock'} />
                {closed ? 'зачислены клиенту' : 'начислятся после оплаты'}
              </span>
            </div>
            <dl className="ui-stats">
              <div>
                <dt>Начислено</dt>
                <dd>+{formatAmount(item.cashback)}</dd>
              </div>
              <div>
                <dt>Списано</dt>
                <dd>{item.spend ? `−${formatAmount(item.spend)}` : '0'}</dd>
              </div>
            </dl>
            {ledgerEntries.length ? (
              <div className="ui-card-body od-facts-body">
                <div className="ui-facts">
                  {ledgerEntries.map((entry) => (
                    <div key={entry.id}>
                      <span className="k">
                        {ledgerText(entry.type)}
                        <span className="od-when">{formatDayTime(entry.at)}</span>
                      </span>
                      <span className="v od-num">
                        {entry.amount > 0 ? '+' : ''}
                        {formatAmount(entry.amount)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </section>
        </div>
      </div>

      {issuing ? (
        <Modal
          title={`Выдать заказ № ${item.number}`}
          onClose={() => {
            setIssuing(false)
          }}
        >
          {/* Способ оплаты известен: сервер требует подтвердить деньги только у
              оплаты в аптеке, поэтому и вариант ровно один (R-34). */}
          {cashAtPharmacy ? (
            <p>
              Оплата в аптеке — отметьте, что деньги получены: сумма запишется на фармацевта, и
              только после этого начислится кэшбэк (R-34).
            </p>
          ) : null}
          <div className="ord-choices">
            <button
              type="button"
              className="ui-btn ord-choice"
              data-variant="secondary"
              onClick={() => {
                issue.mutate({ id, cashCollected: cashAtPharmacy }, handlers)
                setIssuing(false)
              }}
            >
              {cashAtPharmacy ? 'Выдал, деньги получены' : 'Выдал, заказ уже оплачен'}
              <Icon name="i-back" className="go" />
            </button>
          </div>
          <div className="mfoot">
            <button
              type="button"
              className="ui-btn"
              data-variant="secondary"
              onClick={() => {
                setIssuing(false)
              }}
            >
              Закрыть
            </button>
          </div>
        </Modal>
      ) : null}

      {declining ? (
        <Modal
          title={`Отказаться от заказа № ${item.number}`}
          onClose={() => {
            setDeclining(false)
          }}
        >
          <p>
            Заказ уйдёт в ближайшую аптеку, где есть всё и которая ещё не отказывалась. Если таких
            нет — к сотруднику всей сети (R-37, R-38).
          </p>
          <div className="ui-form">
            <div className="ui-field">
              <span className="ui-label" aria-hidden="true">
                Причина
              </span>
              <Select
                label="Причина"
                value={reason}
                options={DECLINE_REASONS}
                onChange={setReason}
              />
            </div>
            <div className="ui-field">
              <label className="ui-label" htmlFor="decline-comment">
                Комментарий
              </label>
              <AutoTextarea
                id="decline-comment"
                placeholder="Что сказать клиенту — необязательно"
                value={comment}
                onChange={(e) => {
                  setComment(e.target.value)
                }}
              />
            </div>
          </div>
          <div className="mfoot">
            <button
              type="button"
              className="ui-btn"
              data-variant="secondary"
              onClick={() => {
                setDeclining(false)
              }}
            >
              Отмена
            </button>
            <button
              type="button"
              className="ui-btn"
              data-variant="danger"
              onClick={() => {
                decline.mutate({ id, reason, comment }, handlers)
                setDeclining(false)
                setComment('')
              }}
            >
              Отказаться от заказа
            </button>
          </div>
        </Modal>
      ) : null}

      {releasing ? (
        <Modal
          title={`Отменить заказ № ${item.number}`}
          onClose={() => {
            setReleasing(false)
          }}
        >
          <p>
            {formatMoney(item.total)} разблокируются на карте клиента, баллы и товар вернутся. Заказ
            станет «{statusText('cancelled')}» — это не отменить.
          </p>
          <div className="mfoot">
            <button
              type="button"
              className="ui-btn"
              data-variant="secondary"
              onClick={() => {
                setReleasing(false)
              }}
            >
              Не отменять
            </button>
            <button
              type="button"
              className="ui-btn"
              data-variant="danger"
              onClick={() => {
                release.mutate(id, handlers)
                setReleasing(false)
              }}
            >
              Отменить и вернуть деньги
            </button>
          </div>
        </Modal>
      ) : null}

      {sheet ? <OrderSheet sheet={sheet} /> : null}
    </>
  )
}

/**
 * Бумажный лист заказа (F-35). На экране его нет — он появляется только при
 * печати, поэтому стоит прямо в `body`: так печать прячет всё остальное
 * приложение одним правилом, не зная о каркасе.
 */
export function OrderSheet({ sheet }: { sheet: PrintSheet }) {
  const { order, organization } = sheet
  const contacts = [organization.phone, organization.email].filter(Boolean).join(' · ')
  return createPortal(
    <article className="od-sheet">
      <header className="od-sheet-org">
        {organization.name ? <b>{organization.name}</b> : null}
        {contacts ? <span>{contacts}</span> : null}
      </header>
      <h1>Заказ № {order.number}</h1>
      <p>
        {formatDayTime(order.createdAt)} · {shipText(order.ship)} · {statusText(order.status)}
        {order.hasRx ? ' · ℞ в заказе' : ''}
      </p>
      <dl>
        <div>
          <dt>Клиент</dt>
          <dd>
            {order.customerName || '—'} {formatPhone(order.customerPhone)}
          </dd>
        </div>
        <div>
          <dt>{order.ship === 'delivery' ? 'Адрес доставки' : 'Аптека'}</dt>
          <dd>{(order.ship === 'delivery' ? order.address : order.storeName) || '—'}</dd>
        </div>
        <div>
          <dt>Оплата</dt>
          <dd>
            {payText(order.payMethod) || '—'} · {payStatusText(order.payStatus)}
          </dd>
        </div>
        {order.reservationExpires ? (
          <div>
            <dt>Бронь до</dt>
            <dd>{formatDayTime(order.reservationExpires)}</dd>
          </div>
        ) : null}
      </dl>
      <table>
        <thead>
          <tr>
            <th scope="col">№</th>
            <th scope="col">Товар</th>
            <th scope="col">Цена</th>
            <th scope="col">Кол-во</th>
            <th scope="col">Сумма</th>
          </tr>
        </thead>
        <tbody>
          {order.lines.map((line, i) => (
            <tr key={line.id}>
              <td>{i + 1}</td>
              <td>{line.name}</td>
              <td>{formatAmount(line.price)}</td>
              <td>{line.qty}</td>
              <td>{formatAmount(line.total)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <dl className="od-sheet-totals">
        <div>
          <dt>Товары</dt>
          <dd>{formatMoney(order.goods)}</dd>
        </div>
        <div>
          <dt>{shipText(order.ship)}</dt>
          <dd>{order.deliveryFee ? formatMoney(order.deliveryFee) : 'Бесплатно'}</dd>
        </div>
        {order.spend ? (
          <div>
            <dt>Оплачено баллами</dt>
            <dd>−{formatMoney(order.spend)}</dd>
          </div>
        ) : null}
        <div>
          <dt>Итого к оплате</dt>
          <dd>{formatMoney(order.total)}</dd>
        </div>
      </dl>
      <footer>Напечатано {formatDayTime(sheet.printedAt)}</footer>
    </article>,
    document.body,
  )
}
